/**
 * SELECTION PANEL
 *
 * Displays details when a project, agent, or zone is selected.
 * Shows project info, agents, tasks, and actions.
 * Supports edit mode for moving/resizing projects and zones.
 */

import { useState, useRef, useCallback } from "react";
import { useWorldStore, type ProjectShape, type PipelineDirection, type PipelineStyle, type PipelineTraffic } from "@/stores/worldStore";
import { useSelectionStore } from "@/stores/selectionStore";
import { useUIStore } from "@/stores/uiStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  X, Activity, Clock, Users, Zap, Pause, Timer,
  Terminal, Kanban, Github, ExternalLink, Server, Container,
  Edit, Move, Square, Circle, Hexagon, Database, Cog, Factory,
  RectangleHorizontal, Upload, ChevronDown, ChevronUp, AlertCircle,
  CheckCircle, HelpCircle, XCircle, Palette, ArrowRight, Plus, Trash2,
  Maximize2
} from "lucide-react";
import { useLocation } from "wouter";
import { setLastVisitedProject } from "./ProjectDetailView";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useSidebarStore } from "@/stores/sidebarStore";

const statusColors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  healthy: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/20", dot: "bg-green-400" },
  degraded: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/20", dot: "bg-amber-400" },
  failing: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/20", dot: "bg-red-400" },
};

const stateConfig = {
  idle: { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/20", icon: Pause, label: "Idle" },
  busy: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/20", icon: Zap, label: "Busy" },
  queued: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/20", icon: Timer, label: "Queued" },
};

const issueTypeColors: Record<string, { bg: string; text: string; border: string }> = {
  feature: { bg: "bg-[#00B2C8]/15", text: "text-[#00B2C8]", border: "border-[#00B2C8]/20" },
  bug: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/20" },
  wp: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/20" },
  hotfix: { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/20" },
};

const attentionConfig = {
  none: { icon: null, bg: "", text: "", border: "", label: "" },
  completed: { icon: CheckCircle, bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/20", label: "Completed" },
  stuck: { icon: AlertCircle, bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/20", label: "Stuck" },
  human_needed: { icon: HelpCircle, bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/20", label: "Human Needed" },
  error: { icon: XCircle, bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/20", label: "Error" },
};

const shapeOptions: { value: ProjectShape; label: string; icon: typeof Square }[] = [
  { value: "square", label: "Square", icon: Square },
  { value: "circle", label: "Circle", icon: Circle },
  { value: "rectangle", label: "Rectangle", icon: RectangleHorizontal },
  { value: "hexagon", label: "Hexagon", icon: Hexagon },
  { value: "gear", label: "Gear", icon: Cog },
  { value: "database", label: "Database", icon: Database },
  { value: "server", label: "Server", icon: Server },
  { value: "factory", label: "Factory", icon: Factory },
];

export function SelectionPanel() {
  const { projects, agents, tasks, zones, pipelines, updateProject, updateZone, removeZone, addPipeline, removePipeline, removeAgent } = useWorldStore();
  const { selectedType, selectedId, clearSelection, selectAgent, openKanbanForProject } = useSelectionStore();
  const { editMode, toggleEditMode } = useUIStore();
  const [expandedMessage, setExpandedMessage] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState("");
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [pipelineTarget, setPipelineTarget] = useState<number | null>(null);
  const [pipelineDirection, setPipelineDirection] = useState<PipelineDirection>("forward");
  const [pipelineStyle, setPipelineStyle] = useState<PipelineStyle>("solid");
  const [pipelineTraffic, setPipelineTraffic] = useState<PipelineTraffic>("normal");
  const [, setLocation] = useLocation();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { setSelectedProjectSubTab } = useSidebarStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shapeImageInputRef = useRef<HTMLInputElement>(null);

  const navigateToProject = useCallback((projectId: number) => {
    if (activeWorkspaceId) {
      setLastVisitedProject(activeWorkspaceId, projectId);
    }
    const project = projects.find(p => p.id === projectId);
    if (project?.project_category === "marketing" && project?.project_subtype === "content") {
      setLocation(`/projects/${projectId}/content`);
    } else {
      setLocation("/projects");
    }
  }, [activeWorkspaceId, setLocation, projects]);

  if (!selectedType || !selectedId) return null;

  if (selectedType === "agent") {
    const agent = agents.find((a) => a.id === selectedId);
    if (!agent) return null;

    const parentProject = projects.find((p) => p.id === agent.projectId);
    const agentTask = tasks.find((t) => t.id === agent.taskId);
    const config = stateConfig[agent.state];
    const StateIcon = config.icon;
    const agentConfig = agent.config || { profile: "auto", humanReviewRequired: true, autoCommit: false, autoPush: false };
    const attentionState = agent.attentionState || "none";
    const attention = attentionConfig[attentionState] || attentionConfig.none;
    const AttentionIcon = attention.icon;

    const profileLabels: Record<string, { label: string; description: string }> = {
      auto: { label: "Auto", description: "Automatically selects best model" },
      fast: { label: "Fast", description: "Quick responses, lower cost" },
      balanced: { label: "Balanced", description: "Good balance of speed and quality" },
      powerful: { label: "Powerful", description: "Best quality, higher cost" },
    };

    return (
      <div
        className="absolute top-0 right-0 w-[300px] h-full border-l border-[rgba(255,255,255,0.08)] z-50 flex flex-col"
        style={{
          background: '#1A1A1E',
          animation: 'slideInRight 0.25s ease-out',
        }}
        data-testid="panel-agent-selection"
      >
        <div className="h-11 flex items-center justify-between px-3 border-b border-[rgba(255,255,255,0.06)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${agent.state === "busy" ? "bg-blue-500 animate-pulse" : agent.state === "queued" ? "bg-purple-500" : "bg-slate-400"}`} data-testid="indicator-agent-active" />
            <span className="text-sm font-semibold text-[#F3F4F6]" data-testid="text-agent-name">
              {agent.name || `Agent ${agent.id}`}
            </span>
          </div>
          <button
            onClick={clearSelection}
            aria-label="Close agent panel"
            className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
            data-testid="button-close-agent-panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${config.bg} ${config.text} border ${config.border}`} data-testid="badge-agent-state">
              <StateIcon className="w-3 h-3" />
              {config.label}
            </span>
            {agent.pipelineStage && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium capitalize bg-blue-500/15 text-blue-400 border border-blue-500/20">
                {agent.pipelineStage}
              </span>
            )}
            {attentionState !== "none" && AttentionIcon && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${attention.bg} ${attention.text} border ${attention.border}`} data-testid="badge-attention-state">
                <AttentionIcon className="w-3 h-3" />
                {attention.label}
              </span>
            )}
          </div>

          {attentionState !== "none" && agent.attentionMessage && (
            <div className="space-y-2 p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]" data-testid="container-attention-message">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {AttentionIcon && <AttentionIcon className="w-4 h-4 text-[#9CA3AF]" />}
                  <span className="text-sm font-medium text-[#E5E7EB]">{agent.attentionMessage}</span>
                </div>
                {agent.attentionDetails && (
                  <button
                    onClick={() => setExpandedMessage(!expandedMessage)}
                    aria-label={expandedMessage ? "Collapse message" : "Expand message"}
                    aria-expanded={expandedMessage}
                    className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                    data-testid="button-toggle-message-expand"
                  >
                    {expandedMessage ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {expandedMessage && agent.attentionDetails && (
                <div className="text-xs text-[#6B7280] bg-[rgba(0,0,0,0.2)] p-2 rounded-md" data-testid="text-attention-details">
                  {agent.attentionDetails}
                </div>
              )}
            </div>
          )}

          {agentTask && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div className="space-y-2" data-testid="container-agent-task-details">
                <div className="text-sm font-medium text-[#E5E7EB]" data-testid="text-task-title">{agentTask.title}</div>
                {agentTask.description && (
                  <div className="text-xs text-[#6B7280]" data-testid="text-task-description">
                    {agentTask.description}
                  </div>
                )}
                {agentTask.githubIssueUrl && (
                  <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors" data-testid="button-view-issue">
                    <Github className="w-3 h-3" />
                    View Issue
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            </>
          )}

          {agent.currentPrompt && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div className="space-y-1" data-testid="container-agent-prompt">
                <div className="text-xs font-medium text-[#6B7280]">Current Prompt</div>
                <div className="text-xs bg-[rgba(0,0,0,0.2)] p-2 rounded-md font-mono max-h-24 overflow-auto text-[#9CA3AF]" data-testid="text-current-prompt">
                  {agent.currentPrompt}
                </div>
              </div>
            </>
          )}

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-3" data-testid="container-agent-config">
            <div className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">Agent Configuration</div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#9CA3AF]">AI Profile</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium capitalize bg-[rgba(255,255,255,0.04)] text-[#E5E7EB] border border-[rgba(255,255,255,0.08)]" data-testid="badge-agent-profile">
                  {profileLabels[agentConfig.profile]?.label || agentConfig.profile}
                </span>
              </div>
              <div className="text-xs text-[#6B7280]">
                {profileLabels[agentConfig.profile]?.description}
              </div>
              {agentConfig.model && (
                <div className="text-xs text-[#6B7280]">
                  Model: <span className="font-mono text-[#9CA3AF]">{agentConfig.model}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#6B7280]" />
                <span className="text-sm text-[#9CA3AF]">Human Review Required</span>
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                  agentConfig.humanReviewRequired
                    ? "bg-amber-500/15 text-amber-400 border-amber-500/20"
                    : "bg-green-500/15 text-green-400 border-green-500/20"
                }`}
                data-testid="badge-human-review"
              >
                {agentConfig.humanReviewRequired ? "Yes" : "No"}
              </span>
            </div>

            <div className="h-px bg-[rgba(255,255,255,0.06)]" />
            <div className="space-y-2" data-testid="container-git-options">
              <div className="text-xs font-medium text-[#6B7280]">Git Options</div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5" data-testid="indicator-auto-commit">
                  <div className={`w-2 h-2 rounded-full ${agentConfig.autoCommit ? "bg-green-500" : "bg-[#4B5563]"}`} />
                  <span className={agentConfig.autoCommit ? "text-[#E5E7EB]" : "text-[#6B7280]"}>Auto-commit</span>
                </div>
                <div className="flex items-center gap-1.5" data-testid="indicator-auto-push">
                  <div className={`w-2 h-2 rounded-full ${agentConfig.autoPush ? "bg-green-500" : "bg-[#4B5563]"}`} />
                  <span className={agentConfig.autoPush ? "text-[#E5E7EB]" : "text-[#6B7280]"}>Auto-push</span>
                </div>
              </div>
            </div>
          </div>

          {parentProject && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div className="flex items-center gap-2 text-sm" data-testid="container-agent-project">
                <span className="text-[#6B7280]">Assigned to:</span>
                <span className="font-medium text-[#E5E7EB]">{parentProject.name}</span>
                {(() => {
                  const sc = statusColors[parentProject.status] || statusColors.healthy;
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text} border ${sc.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                      {parentProject.status}
                    </span>
                  );
                })()}
              </div>
            </>
          )}

          {agent.startedAt && (
            <div className="flex items-center gap-2 text-xs text-[#6B7280]">
              <Clock className="w-3 h-3" />
              Started: {agent.startedAt}
              {agent.estimatedCompletion && (
                <span>· Est. completion: {agent.estimatedCompletion}</span>
              )}
            </div>
          )}

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="pt-1">
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              onClick={() => {
                removeAgent(agent.id);
                clearSelection();
              }}
              data-testid="button-delete-agent"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Agent
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedType === "zone") {
    const zone = zones.find((z) => z.id === selectedId);
    if (!zone) return null;

    const zoneProjects = projects.filter((p) => p.zoneId === zone.id);
    const zoneAgents = agents.filter((a) => zoneProjects.some((p) => p.id === a.projectId));

    const handleNameSave = () => {
      if (tempName.trim()) {
        updateZone(zone.id, { name: tempName.trim() });
      }
      setEditingName(false);
    };

    return (
      <div
        className="absolute top-0 right-0 w-[300px] h-full border-l border-[rgba(255,255,255,0.08)] z-50 flex flex-col"
        style={{
          background: '#1A1A1E',
          animation: 'slideInRight 0.25s ease-out',
        }}
        data-testid="panel-zone-selection"
      >
        <div className="h-11 flex items-center justify-between px-3 border-b border-[rgba(255,255,255,0.06)] flex-shrink-0">
          {editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                onBlur={handleNameSave}
                autoFocus
                className="h-7 text-sm"
                data-testid="input-zone-name"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: zone.color.replace(/[\d.]+\)$/, "1)") }} />
              <span className="text-sm font-semibold text-[#F3F4F6]" data-testid="text-zone-name">{zone.name}</span>
              <button
                onClick={() => {
                  setTempName(zone.name);
                  setEditingName(true);
                }}
                aria-label="Edit zone name"
                className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="button-edit-zone-name"
              >
                <Edit className="w-3 h-3" />
              </button>
            </div>
          )}
          <button
            onClick={clearSelection}
            aria-label="Close zone panel"
            className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
            data-testid="button-close-zone-panel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-500/15 text-blue-400 border border-blue-500/20">
              <Container className="w-3 h-3" />
              Zone
            </span>
            <span className="text-xs text-[#6B7280]">
              {zoneProjects.length} projects · {zoneAgents.length} agents
            </span>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#9CA3AF]">Edit Mode</span>
              <Button
                size="sm"
                variant={editMode ? "default" : "outline"}
                onClick={toggleEditMode}
                className="gap-1"
                data-testid="button-toggle-zone-edit-mode"
              >
                <Move className="w-3 h-3" />
                {editMode ? "Editing" : "Edit"}
              </Button>
            </div>
            {editMode && (
              <p className="text-xs text-[#6B7280]">
                Drag the zone to move it. Drag the corner handle to resize.
              </p>
            )}
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <Label className="text-xs text-[#6B7280]">Zone Color</Label>
            <div className="flex gap-2 flex-wrap">
              {["rgba(59, 130, 246, 0.15)", "rgba(168, 85, 247, 0.15)", "rgba(34, 197, 94, 0.15)", "rgba(245, 158, 11, 0.15)", "rgba(239, 68, 68, 0.15)", "rgba(14, 165, 233, 0.15)"].map((color) => (
                <button
                  key={color}
                  onClick={() => updateZone(zone.id, { color })}
                  className={`w-6 h-6 rounded border-2 transition-colors ${zone.color === color ? "border-[#F3F4F6]" : "border-transparent hover:border-[rgba(255,255,255,0.2)]"}`}
                  style={{ backgroundColor: color.replace(/[\d.]+\)$/, "1)") }}
                  data-testid={`button-zone-color-${color}`}
                />
              ))}
            </div>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div>
            <div className="text-sm font-medium text-[#E5E7EB] mb-2">Projects in Zone</div>
            <div className="max-h-24 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
              {zoneProjects.map((project) => {
                const sc = statusColors[project.status] || statusColors.healthy;
                return (
                  <div key={project.id} className="flex items-center gap-2 p-2 rounded-md bg-[rgba(255,255,255,0.03)] text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${sc.bg} ${sc.text} border ${sc.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                      {project.status}
                    </span>
                    <span className="flex-1 truncate text-[#E5E7EB]">{project.name}</span>
                  </div>
                );
              })}
              {zoneProjects.length === 0 && (
                <div className="text-xs text-[#6B7280] text-center py-2">
                  No projects in this zone
                </div>
              )}
            </div>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div>
            <Button
              variant="destructive"
              size="sm"
              className="w-full gap-2"
              onClick={async () => {
                const success = await removeZone(zone.id);
                if (success) {
                  clearSelection();
                }
              }}
              data-testid="button-delete-zone"
            >
              <Trash2 className="w-4 h-4" />
              Delete Zone
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedType === "project") {
    const project = projects.find((p) => p.id === selectedId);
    if (!project) return null;

    const projectAgents = agents.filter((a) => a.projectId === project.id);
    const projectTasks = tasks.filter((t) => t.projectId === project.id);
    const busyCount = projectAgents.filter((a) => a.state === "busy").length;
    const idleCount = projectAgents.filter((a) => a.state === "idle").length;
    const attentionAgents = projectAgents.filter((a) => a.attentionState && a.attentionState !== "none");

    const handleNameSave = () => {
      if (tempName.trim()) {
        updateProject(project.id, { name: tempName.trim() });
      }
      setEditingName(false);
    };

    const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          updateProject(project.id, { icon: reader.result as string });
        };
        reader.readAsDataURL(file);
      }
    };

    const handleShapeImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          updateProject(project.id, { customShapeImage: reader.result as string });
        };
        reader.readAsDataURL(file);
      }
    };

    const currentShape = shapeOptions.find((s) => s.value === project.shape) || shapeOptions[0];
    const ShapeIcon = currentShape.icon;
    const sc = statusColors[project.status] || statusColors.healthy;

    return (
      <div
        className="absolute top-0 right-0 w-[300px] h-full border-l border-[rgba(255,255,255,0.08)] z-50 flex flex-col"
        style={{
          background: '#1A1A1E',
          animation: 'slideInRight 0.25s ease-out',
        }}
        data-testid="panel-project-selection"
      >
        <div className="h-11 flex items-center justify-between px-3 border-b border-[rgba(255,255,255,0.06)] flex-shrink-0">
          {editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                onBlur={handleNameSave}
                autoFocus
                className="h-7 text-sm"
                data-testid="input-project-name"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#F3F4F6]" data-testid="text-project-name">{project.name}</span>
              <button
                onClick={() => {
                  setTempName(project.name);
                  setEditingName(true);
                }}
                aria-label="Edit project name"
                className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="button-edit-project-name"
              >
                <Edit className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigateToProject(project.id)}
              aria-label="Open project"
              className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
              data-testid="button-expand-project-panel"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={clearSelection}
              aria-label="Close project panel"
              className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
              data-testid="button-close-project-panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${sc.bg} ${sc.text} border ${sc.border}`} data-testid="badge-project-status">
              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
              {project.status}
            </span>
            {project.lastDeployed && (
              <span className="text-xs text-[#6B7280] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {project.lastDeployed}
              </span>
            )}
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#9CA3AF]">Edit Mode</span>
              <Button
                size="sm"
                variant={editMode ? "default" : "outline"}
                onClick={toggleEditMode}
                className="gap-1"
                data-testid="button-toggle-edit-mode"
              >
                <Move className="w-3 h-3" />
                {editMode ? "Editing" : "Edit"}
              </Button>
            </div>
            {editMode && (
              <p className="text-xs text-[#6B7280]">
                Drag the project block to move it on the grid.
              </p>
            )}
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <Label className="text-xs text-[#6B7280]">Shape</Label>
            <Select
              value={project.shape || "square"}
              onValueChange={(value) => updateProject(project.id, { shape: value as ProjectShape }, { immediate: true })}
            >
              <SelectTrigger className="h-9" data-testid="select-project-shape">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <ShapeIcon className="w-4 h-4" />
                    <span>{currentShape.label}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {shapeOptions.map((shape) => {
                  const Icon = shape.icon;
                  return (
                    <SelectItem key={shape.value} value={shape.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{shape.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <Label className="text-xs text-[#6B7280]">Custom Icon</Label>
            <div className="flex items-center gap-2">
              {project.icon && (
                <img src={project.icon} alt="Project icon" className="w-8 h-8 rounded object-cover" />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleIconUpload}
                className="hidden"
                data-testid="input-icon-upload"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="button-upload-icon"
              >
                <Upload className="w-3 h-3" />
                {project.icon ? "Change" : "Upload"}
              </button>
              {project.icon && (
                <button
                  onClick={() => updateProject(project.id, { icon: undefined })}
                  aria-label="Remove project icon"
                  className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                  data-testid="button-remove-icon"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div className="space-y-2">
            <Label className="text-xs text-[#6B7280]">Custom Building Image</Label>
            <div className="flex items-center gap-2">
              {project.customShapeImage && (
                <img src={project.customShapeImage} alt="Custom shape" className="w-10 h-12 rounded object-contain bg-[rgba(255,255,255,0.03)]" />
              )}
              <input
                ref={shapeImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleShapeImageUpload}
                className="hidden"
                data-testid="input-shape-image-upload"
              />
              <button
                onClick={() => shapeImageInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="button-upload-shape-image"
              >
                <Upload className="w-3 h-3" />
                {project.customShapeImage ? "Change" : "Upload"}
              </button>
              {project.customShapeImage && (
                <button
                  onClick={() => updateProject(project.id, { customShapeImage: undefined })}
                  className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                  data-testid="button-remove-shape-image"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <p className="text-xs text-[#6B7280]">Upload PNG/SVG to replace the building shape</p>
          </div>

          {project.description && (
            <p className="text-sm text-[#6B7280]">{project.description}</p>
          )}

          <div className="flex gap-2 flex-wrap">
            {project.githubRepo && (
              <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors" data-testid="button-open-github">
                <Github className="w-3 h-3" />
                GitHub
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
            <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors" onClick={() => { setSelectedProjectSubTab("board"); navigateToProject(project.id); }} data-testid="button-open-kanban">
              <Kanban className="w-3 h-3" />
              Kanban
            </button>
            <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors" onClick={() => { setSelectedProjectSubTab("terminal"); navigateToProject(project.id); }} data-testid="button-open-terminal">
              <Terminal className="w-3 h-3" />
              Terminal
            </button>
          </div>

          {project.fargateCluster && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                <div className="flex items-center gap-1">
                  <Server className="w-3 h-3" />
                  {project.fargateCluster}
                </div>
                <div className="flex items-center gap-1">
                  <Container className="w-3 h-3" />
                  {project.containerCount} containers
                </div>
              </div>
            </>
          )}

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div data-testid="container-pipelines">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-[#6B7280]" />
                <span className="text-sm font-medium text-[#E5E7EB]">Pipelines</span>
              </div>
              <button
                onClick={() => setShowAddPipeline(!showAddPipeline)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#9CA3AF] hover:text-[#F3F4F6] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="button-add-pipeline"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            {showAddPipeline && (
              <div className="space-y-3 p-3 bg-[rgba(255,255,255,0.03)] rounded-md mb-2 border border-[rgba(255,255,255,0.06)]">
                <div>
                  <Label className="text-xs text-[#6B7280]">Connect to:</Label>
                  <Select
                    value={pipelineTarget?.toString() || ""}
                    onValueChange={(value) => setPipelineTarget(parseInt(value))}
                  >
                    <SelectTrigger className="h-8 mt-1" data-testid="select-pipeline-target">
                      <SelectValue placeholder="Select project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects
                        .filter((p) => p.id !== project.id)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>
                            {p.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-[#6B7280]">Direction</Label>
                    <Select
                      value={pipelineDirection}
                      onValueChange={(v) => setPipelineDirection(v as PipelineDirection)}
                    >
                      <SelectTrigger className="h-7 mt-1 text-xs" data-testid="select-pipeline-direction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="forward">One-way →</SelectItem>
                        <SelectItem value="backward">One-way ←</SelectItem>
                        <SelectItem value="bidirectional">Two-way ↔</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B7280]">Style</Label>
                    <Select
                      value={pipelineStyle}
                      onValueChange={(v) => setPipelineStyle(v as PipelineStyle)}
                    >
                      <SelectTrigger className="h-7 mt-1 text-xs" data-testid="select-pipeline-style">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Solid</SelectItem>
                        <SelectItem value="dashed">Dashed</SelectItem>
                        <SelectItem value="dotted">Dotted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B7280]">Traffic</Label>
                    <Select
                      value={pipelineTraffic}
                      onValueChange={(v) => setPipelineTraffic(v as PipelineTraffic)}
                    >
                      <SelectTrigger className="h-7 mt-1 text-xs" data-testid="select-pipeline-traffic">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="heavy">Heavy</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      if (pipelineTarget) {
                        addPipeline({
                          fromId: project.id,
                          toId: pipelineTarget,
                          fromType: "project",
                          toType: "project",
                          direction: pipelineDirection,
                          style: pipelineStyle,
                          traffic: pipelineTraffic,
                          animated: pipelineTraffic !== "none",
                        });
                        setShowAddPipeline(false);
                        setPipelineTarget(null);
                        setPipelineDirection("forward");
                        setPipelineStyle("solid");
                        setPipelineTraffic("normal");
                      }
                    }}
                    disabled={!pipelineTarget}
                    className="flex-1"
                    data-testid="button-confirm-pipeline"
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowAddPipeline(false);
                      setPipelineTarget(null);
                    }}
                    data-testid="button-cancel-pipeline"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {(() => {
              const projectPipelines = pipelines.filter(
                (p) => (p.fromType === "project" && p.fromId === project.id) ||
                       (p.toType === "project" && p.toId === project.id)
              );

              if (projectPipelines.length === 0) {
                return (
                  <div className="text-xs text-[#6B7280] text-center py-2">
                    No pipelines connected
                  </div>
                );
              }

              const directionLabels: Record<string, string> = {
                forward: "→",
                backward: "←",
                bidirectional: "↔",
              };
              const trafficColors: Record<string, { bg: string; text: string; border: string }> = {
                heavy: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/20" },
                normal: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/20" },
                light: { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/20" },
                none: { bg: "bg-[rgba(255,255,255,0.04)]", text: "text-[#6B7280]", border: "border-[rgba(255,255,255,0.06)]" },
              };

              return (
                <div className="space-y-1">
                  {projectPipelines.map((pipeline) => {
                    const isSource = pipeline.fromType === "project" && pipeline.fromId === project.id;
                    const targetId = isSource ? pipeline.toId : pipeline.fromId;
                    const targetType = isSource ? pipeline.toType : pipeline.fromType;
                    const target = targetType === "project"
                      ? projects.find((p) => p.id === targetId)
                      : zones.find((z) => z.id === targetId);

                    const dir = pipeline.direction || "forward";
                    const style = pipeline.style || "solid";
                    const traffic = pipeline.traffic || "normal";
                    const tc = trafficColors[traffic] || trafficColors.normal;

                    return (
                      <div
                        key={pipeline.id}
                        className="flex items-center gap-1.5 p-2 rounded-md bg-[rgba(255,255,255,0.03)] text-xs"
                        data-testid={`pipeline-${pipeline.id}`}
                      >
                        <span className="font-medium text-[#E5E7EB]">{directionLabels[dir]}</span>
                        <span className="flex-1 truncate text-[#9CA3AF]">{target?.name || "Unknown"}</span>
                        <span className={`inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium ${tc.bg} ${tc.text} border ${tc.border}`}>
                          {traffic}
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-medium bg-[rgba(255,255,255,0.04)] text-[#9CA3AF] border border-[rgba(255,255,255,0.06)]">
                          {style}
                        </span>
                        <button
                          className="p-1 rounded-md text-[#6B7280] hover:text-[#F3F4F6] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                          onClick={() => removePipeline(pipeline.id)}
                          data-testid={`button-remove-pipeline-${pipeline.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {attentionAgents.length > 0 && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-[#E5E7EB]">{attentionAgents.length} agents need attention</span>
                </div>
                <div className="max-h-16 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
                  {attentionAgents.map((agent) => {
                    const attState = agent.attentionState || "none";
                    const att = attentionConfig[attState] || attentionConfig.none;
                    const AttIcon = att.icon;
                    return (
                      <button
                        key={agent.id}
                        onClick={() => selectAgent(agent.id)}
                        className="w-full flex items-center gap-2 p-2 rounded-md text-left text-sm hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                        data-testid={`button-attention-agent-${agent.id}`}
                      >
                        {AttIcon && <AttIcon className="w-4 h-4 text-[#9CA3AF]" />}
                        <span className="flex-1 truncate text-[#E5E7EB]">{agent.name}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${att.bg} ${att.text} border ${att.border}`}>
                          {att.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#6B7280]" />
              <span className="text-sm font-medium text-[#E5E7EB]">{projectAgents.length} Agents</span>
              <span className="text-xs text-[#6B7280]">
                ({busyCount} busy, {idleCount} idle)
              </span>
            </div>

            <div className="max-h-24 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
              {projectAgents.slice(0, 8).map((agent) => {
                const agentCfg = stateConfig[agent.state];
                const AgentIcon = agentCfg.icon;
                return (
                  <button
                    key={agent.id}
                    onClick={() => selectAgent(agent.id)}
                    className="w-full flex items-center gap-2 p-2 rounded-md text-left text-sm hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                    data-testid={`button-agent-${agent.id}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${
                      agent.state === "idle" ? "bg-[#4B5563]" : agent.state === "busy" ? "bg-blue-500" : "bg-purple-500"
                    }`} />
                    <span className="flex-1 truncate text-[#E5E7EB]">{agent.name || agent.id}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${agentCfg.bg} ${agentCfg.text} border ${agentCfg.border}`}>
                      <AgentIcon className="w-2 h-2" />
                      {agentCfg.label}
                    </span>
                  </button>
                );
              })}
              {projectAgents.length > 8 && (
                <div className="text-xs text-[#6B7280] text-center py-1">
                  +{projectAgents.length - 8} more agents
                </div>
              )}
            </div>
          </div>

          {projectTasks.length > 0 && (
            <>
              <div className="h-px bg-[rgba(255,255,255,0.06)]" />
              <div>
                <div className="text-sm font-medium text-[#E5E7EB] mb-2">Active Tasks ({projectTasks.length})</div>
                <div className="max-h-24 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#2A2A2F transparent' }}>
                  {projectTasks.map((task) => {
                    const tc = issueTypeColors[task.issueType] || issueTypeColors.wp;
                    return (
                      <div key={task.id} className="flex items-center gap-2 p-2 rounded-md bg-[rgba(255,255,255,0.03)] text-sm">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${tc.bg} ${tc.text} border ${tc.border}`}>
                          {task.issueType.toUpperCase()}
                        </span>
                        <span className="flex-1 truncate text-[#E5E7EB]">{task.title}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium capitalize bg-[rgba(255,255,255,0.04)] text-[#9CA3AF] border border-[rgba(255,255,255,0.06)]">
                          {task.pipelineStage}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
